import React, { useMemo, useState } from 'react';
import { Grid, CircularProgress } from '@material-ui/core';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { OnwardButtonIcon, PrimaryButton } from '@/styles/blocks';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { Header1 } from './blocks';
import SubregionListItem from './SubregionListItem';
import Papa from 'papaparse';
import { SUBREGION_IMPORT_REQ_COLUMNS } from './constants';
import SearchInput from '../misc/SearchInput';
import { captureException } from '@sentry/react';
import AddIcon from '@mui/icons-material/Add';

const SubregionList = ({ subregions, selected, loading, filter, callbacks, options, highlightOverride }) => {
    const [editing, setEditing] = useState(null);

    const list = useMemo(() => {
        return subregions
            .map((subregion, idx) => [subregion, idx])
            .filter(([subregion, idx]) => {
                return (
                    !filter.subregion ||
                    callbacks.genName(subregion, idx)?.toLowerCase()?.includes(filter.subregion.toLowerCase()) ||
                    callbacks.genOwner(subregion, idx)?.toLowerCase()?.includes(filter.subregion.toLocaleLowerCase())
                );
            });
    }, [subregions, filter]);

    return (
        <>
            <Grid
                container
                direction="row"
                css={css`
                    flex: 0;
                    flex-basis: 0;
                    flex-wrap: nowrap;
                    padding: 12px 20px;
                `}
            >
                <SearchInput
                    placeholder="Search Subregions"
                    onChange={(search) => callbacks.setFilter((prev) => ({ ...prev, subregion: search }))}
                />
            </Grid>
            <Grid
                container
                direction="row"
                css={css`
                    flex: 1;
                    flex-basis: 0;
                    overflow-y: scroll;
                    border-top: 1px solid ${colors.greys[4]};
                `}
            >
                <Grid container direction="column">
                    <Grid
                        container
                        direction="row"
                        css={css`
                            cursor: pointer;
                            padding: 8px 25px;
                            border-bottom: 1px solid ${colors.greys[4]};
                            background: ${highlightOverride ? colors.greys[4] : colors.white.primary};
                        `}
                        onClick={() => {
                            callbacks.toggleSubregion('ALL');
                        }}
                    >
                        <Header1>{'All'}</Header1>
                    </Grid>
                    {list.map(([subregion, idx]) => {
                        return (
                            <SubregionListItem
                                key={subregion.subregion_id}
                                idx={idx}
                                isEditing={subregion.subregion_id === editing}
                                isSelected={selected?.subregion_id === subregion.subregion_id}
                                subregion={subregion}
                                options={options}
                                callbacks={{
                                    ...callbacks,
                                    toggleEdit: (id) => {
                                        setEditing((prev) => (prev === id ? null : id));
                                    },
                                    setEdit: (id, status = false) => {
                                        setEditing((prev) => (status ? id : null));
                                    },
                                    resetEdit: () => {
                                        setEditing(null);
                                    },
                                }}
                            />
                        );
                    })}
                </Grid>
            </Grid>
            <Grid
                container
                direction="row"
                css={css`
                    padding: 20px 14px;
                    border-top: 1px solid ${colors.greys[4]};
                    flex-wrap: nowrap;
                `}
            >
                <Grid
                    item
                    css={css`
                        padding-right: 1rem;
                        flex-grow: 1;
                    `}
                >
                    <PrimaryButton
                        fullWidth
                        component="label"
                        disabled={!options?.enableImport || loading.import}
                        startIcon={
                            loading.import ? (
                                <CircularProgress size="24px" color="white" />
                            ) : (
                                <FileUploadIcon
                                    css={css`
                                        color: white;
                                    `}
                                />
                            )
                        }
                        css={css`
                            height: 100%;
                        `}
                    >
                        Import
                        <input
                            disabled={!options?.enableImport || loading.import || loading.init}
                            type="file"
                            hidden
                            accept=".csv"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                Papa.parse(file, {
                                    complete: ({ data }) => {
                                        const [headers, ...body] = data;
                                        if (SUBREGION_IMPORT_REQ_COLUMNS.some((header) => !headers.includes(header))) {
                                            callbacks.setNotification({
                                                severity: 'error',
                                                message: `CSV missing required columns: ${SUBREGION_IMPORT_REQ_COLUMNS.join(
                                                    ', '
                                                )}`,
                                            });
                                            return;
                                        }

                                        callbacks.importSubregions(headers, body);
                                    },
                                    error: (e) => {
                                        console.error(e);
                                        captureException(e);
                                        callbacks.setNotification({
                                            severity: 'error',
                                            message: `Error processing CSV file: ${e.message}`,
                                        });
                                    },
                                });
                            }}
                        />
                    </PrimaryButton>
                </Grid>
                <Grid
                    item
                    css={css`
                        padding-right: 1rem;
                        flex-grow: 1;
                    `}
                >
                    <PrimaryButton
                        fullWidth
                        disabled={!options?.enableExport || loading.export || loading.init}
                        onClick={() => callbacks.exportSubregions()}
                        startIcon={
                            <FileDownloadIcon
                                css={css`
                                    color: white;
                                `}
                            />
                        }
                        css={css`
                            height: 100%;
                        `}
                    >
                        Export
                    </PrimaryButton>
                </Grid>
                <Grid item>
                    <OnwardButtonIcon
                        disabled={!options?.enableAddSubregions || loading.subregions}
                        Icon={AddIcon}
                        onClick={() => callbacks.createSubregion({})}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default SubregionList;
